<template>
    <v-container class="pa-5" fluid>
        <page-title>Moje Raporty</page-title>

        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" md="2">
                            <v-select
                                v-model="filters.group_id"
                                label="Grupa"
                                :items="generalGroups"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono grupy"
                                solo
                                clearable
                                hide-details
                                @change="changeFilter()"
                                @click:clear="clearFilter('group_id')"
                            />
                        </v-col>
                        <v-col cols="12" md="4" lg="2">
                            <v-menu
                                v-model="menuFrom"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template #activator="{on}">
                                    <v-text-field
                                        :value="setMonthName(filters.startDateTime__lte)"
                                        label="Data"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        solo
                                        hide-details
                                        append-icon
                                        clearable
                                        class="mt-0"
                                        v-on="on"
                                        @input="changeFilter"
                                        @click:clear="clearFilter('startDateTime__lte')"
                                    >
                                        <template slot="append-outer">
                                            <div class="d-flex flex-wrap">
                                                <v-btn
                                                    icon
                                                    x-small
                                                    class="mt-n2"
                                                    data-sentry-label="Zmiana daty raportu"
                                                    @click="getForwardMonth()"
                                                >
                                                    <v-icon>
                                                        mdi-chevron-up
                                                    </v-icon>
                                                </v-btn>
                                                <v-btn
                                                    icon
                                                    x-small
                                                    data-sentry-label="Zmiana daty raportu"
                                                    @click="getBackwardMonth()"
                                                >
                                                    <v-icon>
                                                        mdi-chevron-down
                                                    </v-icon>
                                                </v-btn>
                                            </div>
                                        </template>
                                    </v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="filters.startDateTime__lte"
                                    type="month"
                                    @change="changeFilter"
                                    @input="menuFrom = false"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="reportsShiftSummaries"
                        :loading="reportsShiftSummariesLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="reportsData"
                        @pagination="updatePagination"
                    >
                        <template #item.startDateTime="{item}">
                            {{ formatDate(item.startDateTime) }}
                        </template>
                        <template #item.endDateTime="{item}">
                            <template v-if="item.endDateTime">
                                {{ formatDate(item.endDateTime) }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.reportedAmount="{item}">
                            {{ item.reportedAmount }}
                        </template>
                        <template #item.finishedReports="{item}">
                            {{ item.finishedReports }}
                        </template>
                        <template #item.shippedBoxes="{item}">
                            {{ item.shippedBoxes }}
                        </template>
                        <template #item.shippedWeight="{item}">
                            {{ item.shippedWeight }}
                        </template>
                        <template #item.workingTimeRatio="{item}">
                            <template v-if="item.workingTimeRatio">
                                <template v-if="item.workingTimeRatio.id === 10">
                                    <v-icon color="green">
                                        mdi-thumb-up-outline
                                    </v-icon>
                                </template>
                                <template v-else-if="item.workingTimeRatio.id === 20">
                                    <v-icon color="red">
                                        mdi-thumb-down-outline
                                    </v-icon>
                                </template>
                                <template v-else>
                                    -
                                </template>
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.duration="{item}">
                            {{ parseDuration(item.duration) }}
                        </template>
                        <template #item.status="{item}">
                            {{ item.status.name }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Szczegóły raportu"
                                            v-on="on"
                                            @click="openReportShiftSummary(item.id)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Szczegóły raportu</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { appConfig } from '@/config.js'
import store from '@/store'
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse.js'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { endOfMonth, addMonths, subMonths, format } from 'date-fns'
import { errorHandler } from '@/utils/errorHandler'


export default {
    name: 'UserReportsShiftSummaries',
    components: {
        PageTitle
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        filters,
        pagination,
        parse,
        dataTableClassSetter
    ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('reports/clearReportsShiftSummaries')
        next()
    },
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('reports/clearUserReportsShiftSummariesFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            menuFrom: false,
            headersStart: [
                {
                    text: 'Start',
                    value: 'startDateTime',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Stop',
                    value: 'endDateTime',
                    sortable: false,
                    align: 'center'
                }
            ],
            headersEnd: [
                {
                    text: 'Sprawność',
                    value: 'workingTimeRatio',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'Zaraportowano',
                    value: 'duration',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Status',
                    value: 'status',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ],
            headersSendings: [
                {
                    text: 'Ilość kartonów',
                    value: 'reportedBoxes',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Waga sumaryczna',
                    value: 'totalReportedWeight',
                    align: 'center',
                    sortable: false,
                }
            ],
            headersReports: [
                {
                    text: 'Nakład',
                    value: 'reportedAmount',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Suma zadań',
                    value: 'finishedReports',
                    sortable: false,
                    align: 'center',
                },
            ]
        }
    },
    computed: {
        headers() {
            if (this.filters.group_id === 'nadawanie') { //sendings
                return this.headersStart.concat(this.headersSendings).concat(this.headersEnd)
            }
            if (this.filters.group_id !== 'nadawanie' && this.filters.group_id !== null) { //reports
                return this.headersStart.concat(this.headersReports).concat(this.headersEnd)
            }

            return this.headersStart.concat(this.headersEnd)
        },
        userId() {
            return this.$store.state.user.userId
        },
        reportsShiftSummaries() {
            return this.$store.state.reports.reports.reportsShiftSummaries
        },
        reportsShiftSummariesLoading() {
            return this.$store.state.reports.reports.reportsShiftSummariesLoading
        },
        generalGroups() {
            return appConfig.reports.generalGroups
        },
        isGroupSelected() {
            return typeof this.filters.group_id !== 'undefined' && this.filters.group_id !== null
        },
        savedFilters() {
            return this.$store.state.reports.reports.userReportsShiftSummariesFilters
        },
        selectedReportShiftSummaryId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] !== null && typeof filters[item] !== 'undefined') {
                    if (item == 'startDateTime__lte') {
                        const selectedMonth = new Date(filters['startDateTime__lte'])
                        const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1)
                        const lastDayOfTheMonth = endOfMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth()))

                        filters['startDateTime__lte'] = `${format(lastDayOfTheMonth, 'YYYY-MM-DD')}T23:59:59Z`
                        filters['startDateTime__gte'] = `${format(startOfMonth, 'YYYY-MM-DD')}T00:00:00Z`
                    }
                    if (item !== 'group_id') {
                        query = query.concat('&', `${item}=${filters[item]}`)
                    }
                }
            }

            return query
        }
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.filters.user_id = this.userId
        this.getReportsShiftSummaries()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedReportShiftSummaryId) {
                return 'isSelected'
            }
        },
        setMonthName() {
            if (this.filters.startDateTime__lte) {
                return new Date(this.filters.startDateTime__lte).toLocaleString('pl', {month: 'long', year: 'numeric'})
            }
        },
        getBackwardMonth() {
            if (Date.parse(this.filters.startDateTime__lte)) {
                const selectedMonth = new Date(this.filters['startDateTime__lte'])
                const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1)
                const previousMonth = subMonths(new Date(startOfMonth.getFullYear(), startOfMonth.getMonth(), startOfMonth.getDate()), 1)

                this.filters['startDateTime__lte'] = format(previousMonth, 'YYYY-MM-DD')
                this.changeFilter()
            }
        },
        getForwardMonth() {
            if (Date.parse(this.filters.startDateTime__lte)) {
                const selectedMonth = new Date(this.filters['startDateTime__lte'])
                const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1)
                const nextMonth = addMonths(new Date(startOfMonth.getFullYear(), startOfMonth.getMonth(), startOfMonth.getDate()), 1)

                this.filters['startDateTime__lte'] = format(nextMonth, 'YYYY-MM-DD')
                this.changeFilter()
            }
        },
        getReportsShiftSummaries() {
            if (this.filters.group_id === 'nadawanie') { //sendings
                this.$store.dispatch('reports/getShipmentsReportsShiftSummaryStatuses')
                    .then(() => {
                        this.$store.dispatch('reports/getShipmentsReportsShiftSummaries', {
                            paginate: this.paginate(),
                            query: this.filterQuery
                        })
                            .then(response => {
                                this.handlePaginationResponse(response)
                            })
                            .catch(error => {
                                errorHandler(error)
                            })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
            }
            if (this.filters.group_id !== 'nadawanie' && this.filters.group_id !== null) { //reports
                this.$store.dispatch('reports/getReportsShiftSummaryStatuses')
                    .then(() => {
                        this.$store.dispatch('reports/getReportsShiftSummaries', {
                            paginate: this.paginate(),
                            query: this.filterQuery
                        })
                            .then(response => {
                                this.handlePaginationResponse(response)
                            })
                            .catch(error => {
                                errorHandler(error)
                            })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
            }
        },
        openReportShiftSummary(reportId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(reportId)
            const routerName = this.filters.group_id !== 'nadawanie'
                ? 'reportsUserReportsShiftSummariesDetailedReports'
                : 'reportsUserShipmentsReportsShiftSummariesDetailedShipmentsReports'

            this.$router.push({
                name: routerName,
                params: {
                    id: reportId,
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        changeFilter() {
            this.$store.dispatch('reports/setUserReportsShiftSummariesFilters', this.filters)
                .catch(error => {
                    errorHandler(error)
                })
            this.resetPagination()

            if (this.isGroupSelected && this.filters.user_id) {
                this.getReportsShiftSummaries()
            }
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && this.filters.user_id && this.isGroupSelected && !this.savedPagination.pagination) {
                this.getReportsShiftSummaries()
            }
            this.clearPaginationPending()
        }
    },
}

</script>